import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiChevronDown,
  FiGlobe,
  FiLogOut,
  FiMoon,
  FiSun,
  FiUser,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../../servicesV2/authService';
import { loginEvent } from '../../Context/events';
import { toast } from '../ui';
import { cn } from '../ui/cn';

/**
 * UserMenu — avatar trigger + popover with theme toggle, language switcher,
 * and logout. Click-outside closes it; Esc also closes.
 */
export function UserMenu({ theme, onToggleTheme }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const translate = async (lang) => {
    await i18n.changeLanguage(lang);
    try {
      localStorage.setItem('language', lang);
    } catch {}
    setOpen(false);
  };

  const logOut = async () => {
    try {
      await logoutUser();
    } catch (err) {
      // Always emit logout even if the network call fails — token might be stale.
      console.error('Logout error:', err);
    } finally {
      loginEvent.emit('login', false);
      toast.info('Déconnecté');
    }
  };

  const name = 'admin betterfly';
  const initials = name
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-line bg-surface py-0.5 pl-0.5 pr-2',
          'text-body-sm text-fg transition-colors hover:bg-ink-50',
          'focus-visible:outline-none focus-visible:shadow-focus-ring',
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-[10px] font-semibold text-brand-700">
          {initials}
        </span>
        <span className="hidden sm:inline truncate max-w-[120px]">{name}</span>
        <FiChevronDown size={14} className="text-fg-subtle" aria-hidden />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -2, scale: 0.98 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full z-40 mt-1.5 w-56 overflow-hidden rounded-lg border border-line bg-surface shadow-e2"
            role="menu"
          >
            <div className="border-b border-line px-3 py-2.5">
              <p className="text-body-sm font-medium text-fg truncate">{name}</p>
              <p className="text-caption text-fg-subtle">Administrator</p>
            </div>

            <MenuItem
              icon={<FiUser size={14} />}
              label={t('GLOBAL.ACCOUNT', { defaultValue: 'Account' })}
              disabled
              hint="Bientôt"
            />

            <MenuItem
              icon={theme === 'dark' ? <FiSun size={14} /> : <FiMoon size={14} />}
              label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              onClick={() => {
                onToggleTheme();
                setOpen(false);
              }}
            />

            <div className="border-t border-line px-2 py-1.5">
              <p className="px-1.5 pb-1 text-micro text-fg-subtle">Language</p>
              <div className="flex gap-1">
                <LangButton active={i18n.language?.startsWith('fr')} onClick={() => translate('fr')}>
                  FR
                </LangButton>
                <LangButton active={i18n.language?.startsWith('en')} onClick={() => translate('en')}>
                  EN
                </LangButton>
                <span className="ml-auto inline-flex items-center text-fg-subtle">
                  <FiGlobe size={14} />
                </span>
              </div>
            </div>

            <div className="border-t border-line">
              <MenuItem
                icon={<FiLogOut size={14} />}
                label={t('GLOBAL.LOGOUT', { defaultValue: 'Log out' })}
                onClick={logOut}
                danger
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuItem({ icon, label, onClick, disabled, danger, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-body-sm',
        'transition-colors',
        disabled
          ? 'cursor-not-allowed text-fg-subtle'
          : danger
            ? 'text-danger-600 hover:bg-danger-50'
            : 'text-fg hover:bg-ink-50',
      )}
    >
      <span className="inline-flex h-4 w-4 items-center justify-center">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="text-caption text-fg-subtle">{hint}</span>}
    </button>
  );
}

function LangButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-md px-2 py-1 text-caption font-medium transition-colors',
        active
          ? 'bg-brand-600 text-white'
          : 'bg-ink-100 text-fg-muted hover:bg-ink-200 hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

export default UserMenu;
